import { useEffect, useRef } from "react";
import { useAuthentication, useTranslation } from "@repo/core";
import { useUpdateUserPreferences, useUserPreferences } from "@repo/api";
import type { PreferredLanguage } from "@repo/api";

const SUPPORTED_LANGUAGES: PreferredLanguage[] = ["en", "cs"];

const isSupportedLanguage = (lng: string): lng is PreferredLanguage =>
  SUPPORTED_LANGUAGES.includes(lng as PreferredLanguage);

/**
 * Keeps the signed-in user's UI language in UsersTable.
 *
 * - Restore: after login, switches to the stored `preferredLanguage` so the
 *   choice follows the user to other devices.
 * - Save: stores the language only when the user switches it manually. The
 *   language detected on startup is never saved - i18next emits that
 *   `languageChanged` during init, before this hook subscribes.
 */
export const usePreferredLanguageSync = () => {
  const { user } = useAuthentication();
  const { i18n } = useTranslation();
  const userId = user?.id;

  const { data: preferences } = useUserPreferences(userId);
  const { updatePreferences } = useUpdateUserPreferences(userId);

  // Language the server holds for the current user. A `languageChanged` event
  // matching it was caused by the restore below, not by the user.
  const syncedLanguageRef = useRef<string | null>(null);
  // User whose stored language was already applied (or overridden by a manual switch)
  const restoredForUserRef = useRef<string | null>(null);

  useEffect(() => {
    syncedLanguageRef.current = null;
  }, [userId]);

  // Restore once per login
  useEffect(() => {
    if (!userId || !preferences || restoredForUserRef.current === userId) return;
    restoredForUserRef.current = userId;

    const saved = preferences.preferredLanguage;
    syncedLanguageRef.current = saved;
    if (saved && saved !== i18n.language) {
      i18n.changeLanguage(saved);
    }
  }, [userId, preferences, i18n]);

  // Save manual switches
  useEffect(() => {
    if (!userId) return;

    const onLanguageChanged = (lng: string) => {
      if (!isSupportedLanguage(lng) || lng === syncedLanguageRef.current) return;

      syncedLanguageRef.current = lng;
      // A manual pick wins over a stored value that is still loading
      restoredForUserRef.current = userId;
      updatePreferences({ preferredLanguage: lng });
    };

    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, [userId, i18n, updatePreferences]);
};

export default usePreferredLanguageSync;
